<template>

    <LoadingSection v-if="!state.support_tickets_loaded && state.support_tickets_loading" />

    <EmptyList v-if="state.support_tickets_loaded && state.support_tickets.length === 0" heading="No Support Tickets" />

    <ul v-if="state.support_tickets_loaded && state.support_tickets.length > 0" role="list" class="divide-y divide-gray-100">
        <li v-for="ticket in state.support_tickets" :key="ticket.id" class="flex flex-wrap items-center justify-between gap-x-6 gap-y-4 py-5 text-sm sm:flex-nowrap">
            <div>
                <p class="font-semibold text-gray-900">
                    <button v-on:click="() => navigateToTicket(ticket.id)" class="hover:text-blue-700">{{ `#${ticket.id}: ${ticket.title}` }}</button>
                </p>
                <div class="mt-1 flex items-center gap-x-2 text-xs text-gray-500">
                    <UserAvatar :user="ticket.assigned_to" size="6" :internal="true" />
                    <p>{{ `${ticket.assigned_to.first_name} ${ticket.assigned_to.last_name}` }}</p>
                    <svg viewBox="0 0 2 2" class="h-[0.2rem] w-[0.2rem] fill-current">
                        <circle cx="1" cy="1" r="1" />
                    </svg>
                    <p><time datetime="2023-01-23T22:34Z">Last updated {{ ticket.time_since_updated }}</time></p>
                    <svg viewBox="0 0 2 2" class="h-[0.2rem] w-[0.2rem] fill-current">
                        <circle cx="1" cy="1" r="1" />
                    </svg>
                    <SupportTicketStatus :status="ticket.status" />
                </div>
            </div>
            <dl class="flex w-full flex-none justify-between gap-x-8 sm:w-auto">
                <div class="flex w-16 gap-x-2.5">
                    <dt>
                        <span class="sr-only">Total comments</span>
                        <svg class="h-5 w-6" :class="{ 'text-gray-900': ticket.comments_count > 0, 'text-gray-400': !ticket.comments_count}" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" data-slot="icon">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12.76c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.076-4.076a1.526 1.526 0 0 1 1.037-.443 48.282 48.282 0 0 0 5.68-.494c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
                        </svg>
                    </dt>
                    <dd class="text-sm/6" :class="{ 'text-gray-900': ticket.comments_count > 0, 'text-gray-400': !ticket.comments_count}">{{ ticket.comments_count || 0 }}</dd>
                </div>
            </dl>
        </li>
    </ul>
    <div v-if="pagination.total > pagination.per_page">
        <button type="button" v-on:click="getPaginationPrev" class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" :class="{'opacity-50 cursor-not-allowed': is_first_page}" :disabled="is_first_page">Newer Tickets</button>
        <button type="button" v-on:click="getPaginationNext" class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50" :class="{'opacity-50 cursor-not-allowed': is_last_page}" :disabled="is_last_page">Older Tickets</button>
    </div>
</template>

<script>
import {onMounted, reactive, computed, watch, onBeforeMount } from 'vue'
import { useSupportStore } from '@sto/support.store'
import { useAuthStore } from '@sto/auth.store'
import { useRouter } from 'vue-router'

import UserAvatar from "@par/user/UserAvatar.vue";
import LoadingSection from "@glo/LoadingSection.vue";
import EmptyList from "@glo/EmptyList.vue";
import SupportTicketStatus from "@par/support/SupportTicketStatus.vue"

export default {
    props: ['mode'],
    components: {
        EmptyList,
        LoadingSection,
        UserAvatar,
        SupportTicketStatus
    },
    setup (props) {

        const SUPPORT_STORE = useSupportStore()
        const AUTH_STORE = useAuthStore()
        const ROUTER = useRouter()

        const state = reactive({
            support_tickets_loading: false,
            support_tickets_loaded: false,
            support_tickets: []
        })

        const pagination = reactive({
            current_page: 1,
            per_page: 8,
            last_page: 1,
            total: 0,
        })

        const is_last_page = computed(() => {
            return pagination.total > 0 && pagination.current_page == pagination.last_page
        })

        const is_first_page = computed(() => {
            return pagination.current_page == 1
        })

        const getPaginationPage = page => {
            if (page > pagination.last_page || page < 1) return
            pagination.current_page = page
            getSupportTickets()
            window.scrollTo({top: 0, behavior: 'smooth'})
        }

        const getParams = () => {
            let params = []

            params.push({ key: 'per_page', value: pagination.per_page })
            params.push({ key: 'page', value: pagination.current_page })

            switch (props.mode) {
                case 'creator':
                    params.push({ key: 'created_by', value: AUTH_STORE.user.id })
                    break
                case 'support':
                    params.push({ key: 'assigned_to', value: AUTH_STORE.user.id })
                    break
            }

            return params
        }

        const getPaginationNext = () => {
            if (!is_last_page.value) getPaginationPage(pagination.current_page + 1)
        }

        const getPaginationPrev = () => {
            if (!is_first_page.value) getPaginationPage(pagination.current_page - 1)
        }

        const resetPagination = () => {
            pagination.current_page = 1
            pagination.per_page = 8
            pagination.last_page = 1
            pagination.total = 0
        }

        const getSupportTickets = async () => {

            if (state.support_tickets_loading) return

            state.support_tickets_loading = true

            try {
                const params = getParams()
                const support_tickets = await SUPPORT_STORE.list(params)
                pagination.current_page = support_tickets?.pagination?.current_page || 1
                pagination.per_page = support_tickets?.pagination?.per_page || 8
                pagination.last_page = support_tickets?.pagination?.last_page || 1
                pagination.total = support_tickets?.pagination?.total || 0
                state.support_tickets = support_tickets?.support_tickets || []

            } catch (error) {
                console.error('SUPPORT_STORE.list error', error)

            } finally {
                state.support_tickets_loading = false
                state.support_tickets_loaded = true
            }
        }

        const navigateToTicket = ticket_id => {
            ROUTER.push({ name: 'support.show', params: { support_ticket_id: ticket_id }})
            SUPPORT_STORE.closePanel()
        }

        onBeforeMount(() => {
            getSupportTickets()
        })

        return {
            is_last_page,
            is_first_page,
            state,
            pagination,
            getPaginationNext,
            getPaginationPrev,
            navigateToTicket
        }
    }
}
</script>